import React, { Dispatch, SetStateAction } from 'react';
import { FieldArray, Form, Formik, FormikErrors } from 'formik';
import { faAdd, faXmark } from '@fortawesome/pro-solid-svg-icons';
import {
  SalvButton,
  SalvProgressCircle,
  SalvRadioToggleButton,
  SalvRadioToggleGroup,
  Size,
} from '@salvapp/ui-components';
import {
  InternalApiEntityType,
  InternalApiLogicalOperator,
  InternalApiSearchOperator,
  InternalApiSearchFilter,
} from '../api-internal';
import { useQueryEntitySchema } from '../schema/entitySchemaActions';
import TransactionSearchFormRow, { operatorsWithoutQuery } from './TransactionSearchFormRow';
import { newSubQueryFilter, TransactionSearchQuery } from './TransactionSearch';
import { SearchParamKeys } from '../common/searchParamKeys';
import { SetURLSearchParams } from 'react-router';
import * as Yup from 'yup';
import { FormikHelpers } from 'formik/dist/types';
import DataLoadingErrorFallback from '../common/errors/DataLoadingErrorFallback';
import { defaultSearchQuery } from './PersonSearch';

export type TransactionSearchFormValues = Pick<TransactionSearchQuery, 'logicalOperator' | 'filters'>;

interface Props {
  isTransactionSearchFetching: boolean;
  initialValues: TransactionSearchFormValues;
  setSubmittedSearch: Dispatch<SetStateAction<TransactionSearchQuery>>;
  setSearchParams: SetURLSearchParams;
}

const validationSchema = Yup.object().shape({
  filters: Yup.array()
    .of(
      Yup.object().shape({
        attributeIdentifier: Yup.string().required('Attribute is required'),
        operator: Yup.mixed<InternalApiSearchOperator>().required('Operator is required'),
        query: Yup.string().when('operator', {
          is: (operator: InternalApiSearchOperator) => operator && !operatorsWithoutQuery.includes(operator),
          then: (schema) => schema.required('Query is required'),
          otherwise: (schema) => schema,
        }),
      })
    )
    .min(1, 'At least one filter is required')
    .required(),
  logicalOperator: Yup.mixed<InternalApiLogicalOperator>().required('Logical operator is required'),
}) as unknown as Yup.Schema<TransactionSearchFormValues>;

const TransactionSearchForm = ({
  isTransactionSearchFetching,
  initialValues,
  setSubmittedSearch,
  setSearchParams,
}: Props) => {
  const {
    data: transactionSchema,
    isPending: isSchemaPending,
    isError: isSchemaError,
  } = useQueryEntitySchema(InternalApiEntityType.TRANSACTION);

  if (isSchemaError) {
    return <DataLoadingErrorFallback message={'Could not load Schema'} />;
  } else if (isSchemaPending) {
    return <SalvProgressCircle ariaLabel={'Loading Schema'} />;
  }

  const handleSubmit = async (
    values: TransactionSearchFormValues,
    { validateForm }: FormikHelpers<TransactionSearchFormValues>
  ) => {
    const validationFailed = Object.keys(await validateForm(values)).length;
    if (validationFailed) {
      return;
    }

    setSubmittedSearch({
      ...values,
      page: defaultSearchQuery.page,
      orderBy: defaultSearchQuery.orderBy,
      direction: defaultSearchQuery.direction,
    });
    setSearchParams((prev) => {
      const newParams = new URLSearchParams();
      const caseHandle = prev.get(SearchParamKeys.caseHandle);
      if (caseHandle) {
        newParams.set(SearchParamKeys.caseHandle, caseHandle);
      }
      values.filters.forEach((filter) => {
        newParams.append(SearchParamKeys.attributeIdentifier, filter.attributeIdentifier);
        newParams.append(SearchParamKeys.attributeType, filter.attributeType);
        newParams.append(SearchParamKeys.query, filter.query || '');
        newParams.append(SearchParamKeys.operator, filter.operator);
        newParams.append(SearchParamKeys.not, filter.not.toString());
      });

      newParams.set(SearchParamKeys.logicalOperator, values.logicalOperator);
      return newParams;
    });
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
      validateOnChange={false}
    >
      {({ values, setFieldValue, setValues, errors }) => {
        return (
          <Form>
            <FieldArray name='filters'>
              {({ remove, push }) => (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <div style={{ display: 'flex', gap: '16px' }} className={'m-b-8'}>
                    <SalvRadioToggleGroup
                      value={values.logicalOperator}
                      ariaLabel={'Logical operator'}
                      size={Size.SMALL}
                      onChange={async (value) => {
                        await setFieldValue('logicalOperator', value);
                      }}
                    >
                      <SalvRadioToggleButton label={'Match all filters'} value={InternalApiLogicalOperator.AND} />
                      <SalvRadioToggleButton label={'Match at least one'} value={InternalApiLogicalOperator.OR} />
                    </SalvRadioToggleGroup>
                  </div>

                  <div className={'m-b-8'} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {values.filters.map((filter, index) => (
                      <div key={index} style={{ display: 'flex', gap: '8px' }}>
                        <TransactionSearchFormRow
                          index={index}
                          filter={filter}
                          setFieldValue={setFieldValue}
                          setValues={setValues}
                          schemaFields={transactionSchema.schemaFields}
                          errors={errors.filters?.[index] as FormikErrors<InternalApiSearchFilter> | undefined}
                        />
                        {values.filters.length > 1 && (
                          <SalvButton
                            ariaLabel='Remove filter'
                            className={'me-1'}
                            variant='muted'
                            icon={faXmark}
                            size={Size.SMALL}
                            onClick={() => remove(index)}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }} className={'m-b-16'}>
                    <SalvButton
                      variant='muted'
                      icon={faAdd}
                      label={'Add filter'}
                      size={Size.SMALL}
                      onClick={() => push(newSubQueryFilter)}
                      disabled={values.filters.length >= 10}
                    />
                    <SalvButton size={Size.SMALL} type='submit' disabled={isTransactionSearchFetching} label='Search' />
                  </div>
                </div>
              )}
            </FieldArray>
          </Form>
        );
      }}
    </Formik>
  );
};

export default TransactionSearchForm;
